import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    Skeleton,
    Stack,
    Typography,
} from '@mui/material';
import * as React from 'react';

import { DeveloperApiError, IntegrationsApi } from '../api';
import { describeError, mono } from '../components';
import type { TagCategorySeedResult } from '../integrationTypes';
import { useAction } from './IntegrationsContext';
import {
    changedFields, DefaultValues, fieldState, FLAG_FIELDS, flagLabel, onLabels, toggleField,
} from './tagCategoryFlags';

// The grid's dialogs (backend plan 2026-10-29 sdtc WP-G): a default's settings (Add / Edit), Remove, and
// "Add all recommended" (the seed's preview, then the seed).

/** A refused write in words: a 403 says the server's `detail` (it names what is missing, e.g. a waiting sandbox
 *  admin's access group); a 400 `defaults_invalid` lists each refused field under its label. */
export const WriteError = ({ error, flagLabels }: { error: unknown; flagLabels: Record<string, string> }) => {
    if (!error) {
        return null;
    }
    const api = error instanceof DeveloperApiError ? error : null;
    const text = api && api.status === 403 ? (api.body?.detail || api.message) : describeError(error);
    const lines = api && api.status !== 403
        ? api.errors.map((entry) => (entry.field ? `${flagLabel(entry.field, flagLabels)}: ${entry.message}` : entry.message)).filter(Boolean)
        : [];
    return (
        <Alert severity="error" sx={{ mt: 1.5 }} data-testid="tag-category-error">
            {text}
            {lines.length > 0 && (
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {lines.map((line: string) => <li key={line}>{line}</li>)}
                </Box>
            )}
        </Alert>
    );
};

const FlagChips = ({ labels }: { labels: string[] }) => (
    labels.length ? (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ rowGap: 0.5 }}>
            {labels.map((label) => <Chip key={label} size="small" variant="outlined" label={label} />)}
        </Stack>
    ) : <Typography variant="caption" color="text.secondary">Every setting off</Typography>
);

/** A default row's settings: Trip defaults' checkboxes and rules (`tagCategoryFlags.ts`). Add sends every field
 *  (an absent flag is off on the server too, but explicit is what was shown); Edit sends `{from, to}` for the
 *  changed ones only. */
export const TagCategoryFlagsDialog = ({ mode, name, code, intro, initial, checkins, boardings, flagLabels, onSave, onClose }: {
    mode: 'add' | 'edit';
    name: string;
    code: string | null;
    intro: string;
    initial: DefaultValues;
    /** The other categories of the effective defaults with check-in / boarding on. */
    checkins: number;
    boardings: number;
    flagLabels: Record<string, string>;
    /** Add: every field; Edit: the changed fields as `{from, to}`. */
    onSave: (body: Record<string, any>) => Promise<unknown>;
    onClose: () => void;
}) => {
    const [values, setValues] = React.useState<DefaultValues>(initial);
    const action = useAction();
    const changes = changedFields(initial, values);
    const dirty = Object.keys(changes).length > 0;

    const save = async (ev: React.FormEvent) => {
        ev.preventDefault();
        if (mode === 'edit' && !dirty) {
            return;
        }
        const outcome = await action.run(() => onSave(mode === 'add' ? values : changes));
        if (outcome.ok) {
            onClose();
        }
    };

    return (
        <Dialog open onClose={action.busy ? undefined : onClose} aria-labelledby="tag-category-flags-title" fullWidth maxWidth="md" sx={{ zIndex: 10002 }}>
            <Box component="form" onSubmit={save} data-testid="tag-category-flags">
                <DialogTitle id="tag-category-flags-title">
                    {mode === 'add' ? `Add a default for ${name}` : `Default for ${name}`}
                    {code && <Typography component="span" variant="body2" color="text.secondary" sx={{ ...mono, ml: 1 }}>{code}</Typography>}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText variant="body2" sx={{ mb: 1.5 }}>{intro}</DialogContentText>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 2, rowGap: 0.5 }}>
                        {FLAG_FIELDS.map((field) => {
                            const state = fieldState(values, field.id, { code, checkins, boardings });
                            // A disabled box says why in its hint: the box is described by it.
                            const hintId = state.hint ? `tag-category-flag-hint-${field.id}` : undefined;
                            return (
                                <FormControlLabel
                                    key={field.id}
                                    data-testid={`flag-${field.id}`}
                                    sx={{ alignItems: 'flex-start', mr: 0 }}
                                    disabled={state.disabled || action.busy}
                                    control={(
                                        <Checkbox
                                            size="small"
                                            sx={{ pt: 0.25 }}
                                            checked={state.checked}
                                            onChange={(ev) => {
                                                // Read now: an updater may run after React restored the controlled input.
                                                const { checked } = ev.target;
                                                setValues((prev) => toggleField(prev, field.id, checked, initial));
                                            }}
                                            inputProps={{ 'aria-label': flagLabel(field.id, flagLabels), 'aria-describedby': hintId }}
                                        />
                                    )}
                                    label={(
                                        <Box sx={{ py: 0.25 }}>
                                            <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>{flagLabel(field.id, flagLabels)}</Typography>
                                            <Typography variant="caption" color="text.secondary" component="div" id={hintId}>{state.hint || field.tooltip}</Typography>
                                        </Box>
                                    )}
                                />
                            );
                        })}
                    </Box>
                    <WriteError error={action.error} flagLabels={flagLabels} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={action.busy}>Cancel</Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={action.busy || (mode === 'edit' && !dirty)}
                        startIcon={action.busy ? <CircularProgress size={14} /> : undefined}
                    >
                        {mode === 'add' ? 'Add default' : 'Save'}
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    );
};

/** Remove asks first: the default is the operator's, for every integration and every trip that uses the defaults. */
export const RemoveDefaultDialog = ({ name, flagLabels, onRemove, onClose }: {
    name: string;
    flagLabels: Record<string, string>;
    onRemove: () => Promise<unknown>;
    onClose: () => void;
}) => {
    const action = useAction();
    const remove = async () => {
        const outcome = await action.run(onRemove);
        if (outcome.ok) {
            onClose();
        }
    };
    return (
        <Dialog open onClose={action.busy ? undefined : onClose} aria-labelledby="remove-default-title" fullWidth maxWidth="sm" sx={{ zIndex: 10002 }}>
            <DialogTitle id="remove-default-title">Remove the default for {name}?</DialogTitle>
            <DialogContent>
                <DialogContentText variant="body2">
                    Every integration shares this default, and trips that use the operator defaults stop applying its
                    settings at once: new tags in {name} get every setting off. The category and its tags stay.
                </DialogContentText>
                <WriteError error={action.error} flagLabels={flagLabels} />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={action.busy}>Cancel</Button>
                <Button
                    variant="contained"
                    color="error"
                    onClick={remove}
                    disabled={action.busy}
                    startIcon={action.busy ? <CircularProgress size={14} /> : undefined}
                >
                    Remove
                </Button>
            </DialogActions>
        </Dialog>
    );
};

/** A 409 `seed_not_applicable` (a franchise licensee, a sandbox with production data): its `detail`, said as a plain
 *  message, not as a failure. Null for any other error. */
const seedNotApplicable = (error: unknown): string | null => (
    error instanceof DeveloperApiError && error.status === 409 && error.code === 'seed_not_applicable'
        ? (error.body?.detail || error.message)
        : null
);

/** "Add all recommended" (plan Q19): the seed for this booking system, add-only. The preview (`dry_run`) lists the
 *  defaults it adds, the categories it creates first and the seeded flags an association rule left off (`dropped`);
 *  Add runs it for real. Where the seed does not apply (409 `seed_not_applicable`) it says why, with nothing to apply;
 *  a preview that waited too long for a lock (409 `preview_busy`) says to try again. */
export const SeedDialog = ({ api, integrationId, system, flagLabels, onApplied, onClose }: {
    api: IntegrationsApi;
    integrationId: string;
    system: string;
    flagLabels: Record<string, string>;
    onApplied: (result: TagCategorySeedResult) => void;
    onClose: () => void;
}) => {
    const [preview, setPreview] = React.useState<TagCategorySeedResult | null>(null);
    const [previewError, setPreviewError] = React.useState<unknown>(null);
    const action = useAction();

    React.useEffect(() => {
        let live = true;
        api.seedTagCategories(integrationId, true).then(
            (result) => { if (live) { setPreview(result); } },
            (error) => { if (live) { setPreviewError(error); } },
        );
        return () => { live = false; };
    }, [api, integrationId]);

    const apply = async () => {
        const outcome = await action.run(() => api.seedTagCategories(integrationId, false));
        if (outcome.ok) {
            onApplied(outcome.value);
        }
    };

    const added = preview?.added || [];
    const created = preview?.categories_created || [];
    // The seeded flags an association rule refused (the seed is advisory); absent from an older backend.
    const dropped = preview?.dropped || [];
    const empty = !!preview && added.length === 0 && created.length === 0;
    const count = `${added.length} ${added.length === 1 ? 'default' : 'defaults'}`;
    const notApplicable = seedNotApplicable(previewError) || seedNotApplicable(action.error);

    return (
        <Dialog open onClose={action.busy ? undefined : onClose} aria-labelledby="seed-title" fullWidth maxWidth="sm" sx={{ zIndex: 10002 }}>
            <DialogTitle id="seed-title">Add the defaults recommended for {system}?</DialogTitle>
            <DialogContent data-testid="seed-dialog">
                {!preview && !previewError && (
                    <Box role="status" aria-busy="true" aria-label="Loading what would be added">
                        <Skeleton variant="text" width="60%" />
                        <Skeleton variant="text" width="80%" />
                        <Skeleton variant="text" width="45%" />
                    </Box>
                )}
                {notApplicable && (
                    <DialogContentText variant="body2" data-testid="seed-not-applicable">{notApplicable}</DialogContentText>
                )}
                {!seedNotApplicable(previewError) && <WriteError error={previewError} flagLabels={flagLabels} />}
                {empty && (
                    <DialogContentText variant="body2" data-testid="seed-nothing">
                        Nothing to add: every category recommended for {system} already has a default.
                    </DialogContentText>
                )}
                {preview && !empty && !notApplicable && (
                    <>
                        <DialogContentText variant="body2" sx={{ mb: 1.5 }}>
                            These defaults are added for the whole company (every integration shares them). Defaults that exist
                            already are not changed.
                        </DialogContentText>
                        {created.length > 0 && (
                            <Box sx={{ mb: 1.5 }} data-testid="seed-created">
                                <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>Categories created first</Typography>
                                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                                    {created.map((c) => (
                                        <li key={c.code}>
                                            <Typography variant="body2" component="span">{c.name}</Typography>
                                            <Typography variant="caption" color="text.secondary" component="span" sx={{ ...mono, fontSize: 12, ml: 1 }}>{c.code}</Typography>
                                        </li>
                                    ))}
                                </Box>
                            </Box>
                        )}
                        {added.length > 0 && (
                            <Box data-testid="seed-added">
                                <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>Defaults added</Typography>
                                <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                                    {added.map((a) => (
                                        <Box component="li" key={a.code} sx={{ py: 0.75, borderTop: 1, borderColor: 'divider' }} data-testid={`seed-added-${a.code}`}>
                                            <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>{a.name}</Typography>
                                            <Typography variant="caption" color="text.secondary" component="span" sx={{ ...mono, fontSize: 12, ml: 1 }}>{a.code}</Typography>
                                            <Box sx={{ mt: 0.5 }}><FlagChips labels={onLabels(a.fields, flagLabels)} /></Box>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}
                        {dropped.length > 0 && (
                            <Box sx={{ mt: 1.5 }} data-testid="seed-dropped">
                                <Typography variant="caption" color="text.secondary">
                                    Settings an association rule refused are left off; the rest is added.
                                </Typography>
                                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                                    {dropped.map((d) => (
                                        <li key={`${d.code}:${d.field}`} data-testid={`seed-dropped-${d.code}-${d.field}`}>
                                            <Typography variant="body2" component="span">
                                                not added: <Box component="span" sx={{ ...mono, fontSize: 12 }}>{d.code} {d.field}</Box> — {d.message}
                                            </Typography>
                                        </li>
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </>
                )}
                {!seedNotApplicable(action.error) && <WriteError error={action.error} flagLabels={flagLabels} />}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={action.busy}>{empty || previewError || notApplicable ? 'Close' : 'Cancel'}</Button>
                {preview && !empty && !notApplicable && (
                    <Button
                        variant="contained"
                        onClick={apply}
                        disabled={action.busy}
                        startIcon={action.busy ? <CircularProgress size={14} /> : undefined}
                    >
                        {added.length ? `Add ${count}` : 'Create the categories'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export { FlagChips };
